import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import {
    TimelineParties,
    TimelineParty,
} from '../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import './EngagementHistoryTable.scss';
import moment from 'moment';
import InfoTooltip from '../info-tooltip/info-tooltip';
import { useStateSelector } from '../../store/selectors';
import createNotification from '../../utils/createNotification';
import { EngagementHistory } from '../../models/EngagementHistory/EngagementHistory';
import { EngagementHistoryRecord } from '../../models/EngagementHistory/EngagementHistoryRecord';
import SortingOption from '../Table/models/bootstrap-table-sorting-option';
import { ColumnDescription, SortOrder } from 'react-bootstrap-table-next';
import { sortingFunction } from '../Table/models/table-helper';
import SortingSymbol from '../SortingSymbol/sorting-symbol';
import Helpers from '../../utils/helper';
import { ReceiptPopup, ReceiptViewMode } from '../Receipts/ReceiptPopup';
import { isEventActionClickable } from '../../containers/ConnectMarketplaceDashboard/EventActionAvailabilityHelper';
import { TimelineEvent } from '../../slices/marketplace/timeline/models/timeline-event.model';
import { EngagementData } from '../../slices/marketplace/timeline/models/engagement-data.model';
import { EngagementBuyerActionState } from '../../slices/marketplace/timeline/models/engagement-buyer-action-state.model';
import { GetReceiptByDateRange } from '../GetReceiptByDateRange/GetReceiptByDateRange';
import {
    Button,
    FormGroup,
    InputGroupAddon,
    Modal,
    ModalHeader,
    ModalBody,
} from 'reactstrap';
import ButtonLoader from '../Layout/Buttons/ButtonLoader';
import Rodal from '../Rodal/Rodal';
import { LoiSubmissionPackage } from '../../containers/Admin/BuyerMarketplace/OpportunityDetails/LoiSubmissionPackage/LoiSubmissionPackage';
import { useRef } from 'react';
import DatePicker from 'react-datepicker';
import { Input } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faFilePdf } from '@fortawesome/free-regular-svg-icons';
import BuyerProfilePreviewPopup from '../BuyerProfilePreviewPopup/BuyerProfilePreviewPopup';
import {
    ActionsWithBuyerDocumentToSign,
    TimelineActionType,
} from '../../slices/marketplace/timeline/models/timeline-action-type.model';
import { DocumentDetailsStatus } from '../../slices/marketplace/timeline/models/enums/document-details-status.enum';
import { EngagementBuyer } from '../../slices/marketplace/timeline/models/engagement-buyer.model';
import { EngagementHistoryRecordFeeDetailTypes } from '../../models/EngagementHistory/EngagementHistoryRecordFeeDetailTypes.enum';
import { NotificationFlag } from '../NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../models/NotificationFlags/NotificationFlagsCategories';
import { ManualFeeState } from '../../models/EngagementHistory/ManualFeeState.enum';
import { PostManualMonthlyFeeRequest } from '../../models/EngagementHistory/PostManualMonthlyFeeRequest';
import ManualFeeInvoicePopup from './ManualFeeInvoicePopup/ManualFeeInvoicePopup';

interface ManualFeeDraft {
    completionDate: Date;
    amountDue: string;
    amountPaid: string;
    paymentDate: Date;
    refundAmount: string;
}

type ManualFeeAmountField = 'amountDue' | 'amountPaid' | 'refundAmount';

interface ManualFeeDraftErrors {
    amountDue?: string;
    amountPaid?: string;
    paymentDate?: string;
    refundAmount?: string;
}

interface EngagementHistoryTableProps {
    engagementId: number;
    events: TimelineEvent[];
    engagementData: EngagementData;
    viewParty: TimelineParty;
    onEventClick(eventId: string): void;
    isReadonly: boolean;
    refreshTrigger: boolean;
    currentBuyerActionState?: EngagementBuyerActionState[];
    buyers?: EngagementBuyer[];
    onAdminClickBuyerEvent?(eventId: string, buyerId: number): void;
    sellerId?: number;
    canNavigateToTransaction: boolean;
    advisorCompanyId?: string;
}

interface TableSortingState {
    data: EngagementHistoryRecord[];
    sortField: string;
    sortOrder: string;
}

const ActionColumnField = 'action';
const ManualFeeConfirmPopupWidth = 600;
const ManualFeeDateFormat = 'MM/dd/yyyy';
const ManualFeeDatePlaceholder = 'mm/dd/yyyy';
const ManualFeeApiDateFormat = 'YYYY-MM-DD';
const HttpConflictStatus = 409;
const ManualFeeAmountDueRequiredError = 'Enter the amount due';
const ManualFeeInvalidAmountError = 'Enter a valid amount';
const ManualFeeAmountDuePositiveError = 'Amount due must be greater than 0';
const ManualFeeMissingPaymentDateError = 'Enter the payment date';
const ManualFeeMissingAmountPaidError = 'Enter the amount paid';
const ManualFeeRefundNeedsAmountPaidError = 'Refund requires an amount paid';
const ManualFeeRefundExceedsAmountPaidError = 'Refund cannot exceed Amount Paid';
const ManualFeePostedNotification = 'Monthly engagement fee posted.';
const ManualFeeUpdatedNotification = 'Monthly engagement fee updated.';
const ManualFeeConflictNotification =
    'This row was changed by someone else. The table has been refreshed.';
const ManualFeeGenericSaveError = 'Could not save the monthly engagement fee.';
const ManualFeeInvoiceViewTitle = 'View invoice';
const ManualFeeMissingAdvisorCompanyText =
    'This engagement has no Advisor Company. The invoice prints the company name, wiring instructions and footer, so assign an Advisor Company to the engagement before posting the fee.';

const ManualFeeDateInput = forwardRef(
    ({ value, onClick, placeholder }: any, ref: any) => (
        <div
            ref={ref}
            className={value ? 'btn' : 'btn manual-fee-date-placeholder'}
            onClick={onClick}>
            {value || placeholder}
        </div>
    )
);

const EngagementHistoryTable = (props: EngagementHistoryTableProps) => {
    const axios = useStateSelector((state) => state.core.axios);

    const [engagementHistoy, setEngagementHistoy] =
        useState<EngagementHistory>();
    const [timelineEventsList, setTimelineEventsList] = useState<
        EngagementHistoryRecord[]
    >([]);
    const [selectedTransactionId, setSelectedTransactionId] =
        useState<string>();
    const [selectedInvoicePayee, setSelectedInvoicePayee] = useState(null);
    const [isReceiptVisible, setIsReceiptVisible] = useState(false);
    const [isBuyerProfilePopupVisible, setIsBuyerProfilePopupVisible] =
        useState(false);
    const [selectedBuyerId, setSelectedBuyerId] = useState<number>(null);
    const [sortingOption, setSortingOption] = useState<SortingOption>({
        dataField: 'completionDate',
        order: 'asc',
    });
    const [execStartDate, setExecStartDate] = useState<Date | null>(null);
    const [execEndDate, setExecEndDate] = useState<Date | null>(null);
    const [eventFilter, setEventFilter] = useState('');
    const [partyFilter, setPartyFilter] = useState<number | ''>('');
    const [deliverableFilter, setDeliverableFilter] = useState('');
    const [loiPreviewFormId, setLoiPreviewFormId] = useState<string | null>(null);
    const [manualFeeDrafts, setManualFeeDrafts] = useState<
        Record<string, ManualFeeDraft>
    >({});
    const [manualFeeSavingKeys, setManualFeeSavingKeys] = useState<
        Record<string, boolean>
    >({});
    const [manualFeeConfirmRow, setManualFeeConfirmRow] =
        useState<EngagementHistoryRecord | null>(null);
    const [manualFeeInvoiceRow, setManualFeeInvoiceRow] =
        useState<EngagementHistoryRecord | null>(null);

    const execStartRef = useRef<any>(null);
    const execEndRef = useRef<any>(null);

    useEffect(() => {
        fetchTableData();
    }, [props.refreshTrigger]);

    const getNestedValue = (obj: any, path: string): any => {
        return path.split('.').reduce((current, key) => {
            return current && current[key] !== undefined
                ? current[key]
                : undefined;
        }, obj);
    };

    const getEngagementUserIds = (): number[] => {
        const userIds =
            engagementHistoy?.timelineEvents.map((e) => e.userId) ?? [];
        const uniqueIds = Array.from(new Set(userIds));
        return uniqueIds;
    };

    const fetchTableData = () => {
        axios
            .get(`/api/Marketplace/${props.engagementId}/EngagementHistory`)
            .then((response) => {
                if (response.status !== 200) {
                    createNotification(
                        'An error occured on fetching Engagement History',
                        'error'
                    );
                    return;
                }

                setEngagementHistoy(response.data);
                updateTableData(
                    response.data.timelineEvents ?? [],
                    sortingOption
                );
            });
    };

    const openReceiptPopup = (transactionId: string, payeeId: number) => {
        setSelectedTransactionId(transactionId);
        setSelectedInvoicePayee(payeeId);
        setIsReceiptVisible(true);
    };

    const openBuyerProfile = (buyerId: number) => {
        setSelectedBuyerId(buyerId);
        setIsBuyerProfilePopupVisible(true);
    };

    const closeBuyerProfile = () => {
        setIsBuyerProfilePopupVisible(false);
        setSelectedBuyerId(null);
    };

    const isActionWithBuyerDocumentToComplete = (
        row: EngagementHistoryRecord
    ) => {
        const targetEvent = props.events.find((f) => f.id === row.eventId);

        const isBuyerSignDocumentAction =
            row.responsibleParty === TimelineParty.Buyer &&
            ActionsWithBuyerDocumentToSign.some(
                (actionType: TimelineActionType) =>
                    targetEvent?.linkedAction?.actionType === actionType
            );

        const isBuyerUploadDocumentAction =
            targetEvent?.linkedAction?.actionType ===
            TimelineActionType.BuyerUploadDocument;

        if (isBuyerSignDocumentAction || isBuyerUploadDocumentAction) {
            return true;
        }

        return false;
    };

    const isActionWithDuyberDocumentUpload = (row: EngagementHistoryRecord) => {
        const targetEvent = props.events.find((f) => f.id === row.eventId);

        const result =
            targetEvent.linkedAction?.actionType ===
            TimelineActionType.BuyerUploadDocument;
        return result;
    };

    const isBuyerDocumentCompleted = (row: EngagementHistoryRecord) => {
        if (
            (isActionWithBuyerDocumentToComplete(row) &&
                row.responsibleParty === TimelineParty.Buyer) ||
            isActionWithDuyberDocumentUpload(row)
        ) {
            const targetEvent = props.events.find((f) => f.id === row.eventId);
            const targetBuyer = props.buyers?.find(
                (b) => b.buyerId === row.buyerId
            );
            const targetDocumentId = targetBuyer?.actionState.find(
                (f) => f.actionId === targetEvent.linkedAction?.id
            )?.documentDetailsId;

            const targetDocument = props.engagementData.documentsList.find(
                (document) => document.id === targetDocumentId
            );

            if (
                targetDocument?.status === DocumentDetailsStatus.Completed &&
                targetDocument?.counterParty === TimelineParty.Buyer
            ) {
                return true;
            }
        }

        return false;
    };

    const renderDeliverableCell = (
        row: EngagementHistoryRecord,
        cellContent: string
    ): React.JSX.Element => {
        if (props.viewParty === TimelineParty.VetValue) {
            return renderDeliverableForAdmin(row, cellContent);
        }

        return renderDeliverableForUser(row, cellContent);
    };

    const renderFeeCell = (
        row: EngagementHistoryRecord,
        cellContent: string
    ): React.JSX.Element => {
        const transactionIds =
            row.feeDetails?.transactionIds?.filter((f) => f) || [];
        const uniqueTransactionIds = Array.from(new Set(transactionIds));
        const feeDetailTypesWithPaymentInfo = [
            EngagementHistoryRecordFeeDetailTypes.PaymentInfo,
            EngagementHistoryRecordFeeDetailTypes.Summary,
            EngagementHistoryRecordFeeDetailTypes.RefundInfo,
        ];

        const hasPaymentInfo = feeDetailTypesWithPaymentInfo.includes(
            row.feeDetails?.feeDetailsType
        );

        const eventCodes = uniqueTransactionIds.map(
            (transactionId) =>
                `${NotificationFlagsCategories.MpCode}.${props.engagementId}.${NotificationFlagsCategories.EngagementHistoryCode}.${NotificationFlagsCategories.EngagementAutoPaymentCode}.${row.userId}.${transactionId}`
        );
        const uniqueEventCodes = Array.from(new Set(eventCodes));

        const isPostedManualFee = row.manualFeeState === ManualFeeState.Posted;
        const manualFeeEventCode = `${NotificationFlagsCategories.MpCode}.${props.engagementId}.${NotificationFlagsCategories.EngagementHistoryCode}.${NotificationFlagsCategories.EngagementManualFeeCode}.${row.historyRecordId}`;

        return (
            <div className="d-flex align-items-center">
                <div>{cellContent ?? ''}</div>
                {hasPaymentInfo &&
                    uniqueEventCodes.map((eventCode) => {
                        const transactionId = eventCode.split('.').pop();
                        return (
                            <NotificationFlag
                                key={`${row.eventId}-${transactionId}`}
                                eventCode={eventCode}
                                includePrefix={false}
                            />
                        );
                    })}
                {isPostedManualFee && (
                    <NotificationFlag
                        key={`${row.eventId}-manual-fee`}
                        eventCode={manualFeeEventCode}
                        includePrefix={false}
                    />
                )}
            </div>
        );
    };

    const renderEventCell = (
        row: EngagementHistoryRecord,
        cellContent: string
    ): React.JSX.Element => {
        const servicePeriodStart = row.feeDetails?.servicePeriodStart;
        const servicePeriodEnd = row.feeDetails?.servicePeriodEnd;
        if (!servicePeriodStart || !servicePeriodEnd) {
            return <>{cellContent ?? ''}</>;
        }

        const servicePeriod = `Service period: ${moment(
            servicePeriodStart
        ).format('L')} - ${moment(servicePeriodEnd).format('L')}`;

        return (
            <div className="d-flex align-items-center">
                <div>{cellContent ?? ''}</div>
                <InfoTooltip
                    idText={`engagement-history-service-period-${row.eventId}-${servicePeriodStart}`}
                    text={servicePeriod}
                />
            </div>
        );
    };

    const isPaymentRow = (row: EngagementHistoryRecord): boolean =>
        row.feeDetails?.feeDetailsType ===
            EngagementHistoryRecordFeeDetailTypes.PaymentInfo ||
        row.feeDetails?.feeDetailsType ===
            EngagementHistoryRecordFeeDetailTypes.Summary;

    const formatFeeAmount = (
        row: EngagementHistoryRecord,
        amount: number
    ): string =>
        row.feeDetails?.isFeeAvailable && amount != null
            ? Helpers.GetCurrencyFormat(amount)
            : '';

    const renderDeliverableForAdmin = (
        row: EngagementHistoryRecord,
        cellContent: string
    ): React.JSX.Element => {
        const isSignDocumentEventForBuyer =
            isActionWithBuyerDocumentToComplete(row);

        const targetBuyer = props.buyers?.find(
            (f) => f.buyerId === row.buyerId
        );

        const isLoiRecord = !!row.loiFormId;

        const isClickable = isLoiRecord
            ? true
            : isSignDocumentEventForBuyer && props.viewParty === TimelineParty.VetValue
            ? isBuyerDocumentCompleted(row)
            : isEventActionClickable(
                  row.eventId,
                  props.isReadonly,
                  props.engagementData,
                  props.events,
                  props.viewParty,
                  targetBuyer?.actionState ?? []
              );
        const eventClickHandler = () => {
            if (isLoiRecord) {
                setLoiPreviewFormId(row.loiFormId);
                return;
            }
            row.responsibleParty === TimelineParty.Buyer
                ? props.onAdminClickBuyerEvent(row.eventId, row.buyerId)
                : props.onEventClick(row.eventId);
        };
        return getDeliverableCellElement(
            cellContent,
            isClickable,
            eventClickHandler
        );
    };

    const renderDeliverableForUser = (
        row: EngagementHistoryRecord,
        cellContent: string
    ): React.JSX.Element => {
        if (row.loiFormId) {
            return getDeliverableCellElement(cellContent, true, () =>
                setLoiPreviewFormId(row.loiFormId)
            );
        }
        const isClickable = isEventActionClickable(
            row.eventId,
            props.isReadonly,
            props.engagementData,
            props.events,
            props.viewParty,
            props.currentBuyerActionState ?? []
        );
        return getDeliverableCellElement(cellContent, isClickable, () =>
            props.onEventClick(row.eventId)
        );
    };

    const getDeliverableCellElement = (
        cellContent: string,
        isClickable: boolean,
        eventClickHandler: () => void
    ) => (
        <div
            className={isClickable && 'link-text'}
            onClick={() => {
                if (isClickable) {
                    eventClickHandler();
                }
            }}>
            {cellContent}
        </div>
    );

    const totalCreditableAmount = useMemo(
        () =>
            timelineEventsList.reduce((total, record) => {
                const creditableAmount =
                    record.feeDetails?.isFeeAvailable &&
                    record.feeDetails?.creditableAmount > 0
                        ? record.feeDetails.creditableAmount
                        : 0;
                return total + creditableAmount;
            }, 0),
        [timelineEventsList]
    );

    const isManualFeeRow = (row: EngagementHistoryRecord): boolean =>
        props.viewParty === TimelineParty.VetValue &&
        row.manualFeeState != null;

    const isManualFeeRecord = (row: EngagementHistoryRecord): boolean =>
        row.manualFeeState != null;

    const isPostedManualFeeInvoiceRow = (row: EngagementHistoryRecord): boolean =>
        isManualFeeRecord(row) &&
        row.manualFeeState === ManualFeeState.Posted &&
        row.historyRecordId != null;

    const getManualFeeRowKey = (row: EngagementHistoryRecord): string =>
        `${row.eventId}-${row.feeDetails?.servicePeriodStart}`;

    const getDefaultManualFeeDraft = (
        row: EngagementHistoryRecord
    ): ManualFeeDraft => {
        const feeDetails = row.feeDetails;
        return {
            completionDate: row.completionDate
                ? new Date(row.completionDate)
                : null,
            amountDue: Helpers.GetCurrencyFormat(feeDetails?.amountDue ?? 0),
            amountPaid:
                feeDetails?.amountPaid != null && feeDetails.amountPaid > 0
                    ? Helpers.GetCurrencyFormat(feeDetails.amountPaid)
                    : '',
            paymentDate: feeDetails?.paymentDate
                ? new Date(feeDetails.paymentDate)
                : null,
            refundAmount:
                feeDetails?.refundAmount != null && feeDetails.refundAmount > 0
                    ? Helpers.GetCurrencyFormat(feeDetails.refundAmount)
                    : '',
        };
    };

    const getManualFeeDraft = (row: EngagementHistoryRecord): ManualFeeDraft =>
        manualFeeDrafts[getManualFeeRowKey(row)] ??
        getDefaultManualFeeDraft(row);

    const updateManualFeeDraft = (
        row: EngagementHistoryRecord,
        changes: Partial<ManualFeeDraft>
    ) => {
        const key = getManualFeeRowKey(row);
        setManualFeeDrafts((prev) => ({
            ...prev,
            [key]: { ...(prev[key] ?? getDefaultManualFeeDraft(row)), ...changes },
        }));
    };

    const clearManualFeeDraft = (row: EngagementHistoryRecord) => {
        const key = getManualFeeRowKey(row);
        setManualFeeDrafts((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const parseCurrencyInput = (text: string): number | null => {
        const cleaned = (text ?? '').replace(/[^0-9.-]/g, '');
        if (cleaned === '') {
            return null;
        }

        const parsed = Number(cleaned);
        return Number.isNaN(parsed) ? null : parsed;
    };

    const validateManualFeeDraft = (
        draft: ManualFeeDraft
    ): ManualFeeDraftErrors => {
        const errors: ManualFeeDraftErrors = {};

        const hasAmountDueText = draft.amountDue.trim() !== '';
        const amountDue = parseCurrencyInput(draft.amountDue);
        if (!hasAmountDueText) {
            errors.amountDue = ManualFeeAmountDueRequiredError;
        } else if (amountDue === null) {
            errors.amountDue = ManualFeeInvalidAmountError;
        } else if (amountDue <= 0) {
            errors.amountDue = ManualFeeAmountDuePositiveError;
        }

        const hasAmountPaidText = draft.amountPaid.trim() !== '';
        const amountPaid = parseCurrencyInput(draft.amountPaid);
        if (hasAmountPaidText && (amountPaid === null || amountPaid < 0)) {
            errors.amountPaid = ManualFeeInvalidAmountError;
        }

        const hasPaymentDate = !!draft.paymentDate;
        if (hasAmountPaidText && !hasPaymentDate) {
            errors.paymentDate = ManualFeeMissingPaymentDateError;
        } else if (!hasAmountPaidText && hasPaymentDate) {
            errors.amountPaid = ManualFeeMissingAmountPaidError;
        }

        const hasRefundText = draft.refundAmount.trim() !== '';
        const refundAmount = parseCurrencyInput(draft.refundAmount);
        if (hasRefundText) {
            if (refundAmount === null || refundAmount < 0) {
                errors.refundAmount = ManualFeeInvalidAmountError;
            } else if (refundAmount > 0) {
                if (!hasAmountPaidText || amountPaid === null) {
                    errors.refundAmount = ManualFeeRefundNeedsAmountPaidError;
                } else if (refundAmount > amountPaid) {
                    errors.refundAmount = ManualFeeRefundExceedsAmountPaidError;
                }
            }
        }

        return errors;
    };

    const hasManualFeeErrors = (row: EngagementHistoryRecord): boolean =>
        Object.values(validateManualFeeDraft(getManualFeeDraft(row))).some(
            (message) => !!message
        );

    const isManualFeeSaving = (row: EngagementHistoryRecord): boolean =>
        !!manualFeeSavingKeys[getManualFeeRowKey(row)];

    const setManualFeeSaving = (
        row: EngagementHistoryRecord,
        isSaving: boolean
    ) => {
        const key = getManualFeeRowKey(row);
        setManualFeeSavingKeys((prev) => ({ ...prev, [key]: isSaving }));
    };

    const buildManualFeeSaveRequest = (
        draft: ManualFeeDraft,
        version: string
    ): PostManualMonthlyFeeRequest => {
        const amountPaid = parseCurrencyInput(draft.amountPaid);
        const refundAmount = parseCurrencyInput(draft.refundAmount);
        return {
            completionDate: moment(draft.completionDate).format(
                ManualFeeApiDateFormat
            ),
            amountDue: parseCurrencyInput(draft.amountDue),
            amountPaid: amountPaid != null ? amountPaid : undefined,
            paymentDate: draft.paymentDate
                ? moment(draft.paymentDate).format(ManualFeeApiDateFormat)
                : undefined,
            refundAmount: refundAmount != null ? refundAmount : undefined,
            version,
        };
    };

    const handleManualFeeUpdate = (row: EngagementHistoryRecord) => {
        setManualFeeConfirmRow(row);
    };

    const isManualFeePlaceholder = (row: EngagementHistoryRecord | null): boolean =>
        row?.manualFeeState === ManualFeeState.Placeholder;

    const isAdvisorCompanyMissing = !props.advisorCompanyId;

    const closeManualFeeConfirm = () => {
        if (manualFeeConfirmRow && isManualFeeSaving(manualFeeConfirmRow)) {
            return;
        }

        setManualFeeConfirmRow(null);
    };

    const confirmManualFeeSave = () => {
        const row = manualFeeConfirmRow;
        if (!row || isAdvisorCompanyMissing) {
            return;
        }

        const draft = getManualFeeDraft(row);
        const requestBody = buildManualFeeSaveRequest(draft, row.version);
        const isPosting = row.manualFeeState === ManualFeeState.Placeholder;

        setManualFeeSaving(row, true);
        axios
            .put(
                `/api/Marketplace/${props.engagementId}/EngagementHistory/manual-fees/${row.historyRecordId}`,
                requestBody
            )
            .then(() => {
                setManualFeeConfirmRow(null);
                clearManualFeeDraft(row);
                fetchTableData();
                createNotification(
                    isPosting
                        ? ManualFeePostedNotification
                        : ManualFeeUpdatedNotification,
                    'success'
                );
            })
            .catch((error: any) => {
                setManualFeeConfirmRow(null);
                if (error.response?.status === HttpConflictStatus) {
                    createNotification(ManualFeeConflictNotification, 'error');
                    fetchTableData();
                    return;
                }

                createNotification(
                    error.response?.data?.title ||
                        error.response?.data?.message ||
                        ManualFeeGenericSaveError,
                    'error'
                );
            })
            .finally(() => setManualFeeSaving(row, false));
    };

    const openManualFeeInvoice = (row: EngagementHistoryRecord) => {
        setManualFeeInvoiceRow(row);
    };

    const closeManualFeeInvoice = () => {
        setManualFeeInvoiceRow(null);
    };

    const renderManualFeeInvoiceViewButton = (
        row: EngagementHistoryRecord
    ): React.JSX.Element => (
        <button
            type="button"
            title={ManualFeeInvoiceViewTitle}
            className="manual-fee-invoice-download"
            onClick={() => openManualFeeInvoice(row)}>
            <FontAwesomeIcon icon={faFilePdf} />
        </button>
    );

    const renderManualFeeFieldError = (
        message?: string
    ): React.JSX.Element =>
        message ? <div className="manual-fee-error">{message}</div> : <></>;

    const renderManualFeeDatePicker = (
        selected: Date,
        onChange: (value: Date) => void,
        errorMessage?: string
    ): React.JSX.Element => (
        <div className="manual-fee-field">
            <div
                className={
                    errorMessage
                        ? 'd-flex small-border manual-fee-date manual-fee-input-invalid'
                        : 'd-flex small-border manual-fee-date'
                }>
                <InputGroupAddon addonType="prepend">
                    <div className="input-group-text">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                    </div>
                </InputGroupAddon>
                <DatePicker
                    className="form-control"
                    selected={selected}
                    dateFormat={ManualFeeDateFormat}
                    placeholderText={ManualFeeDatePlaceholder}
                    onChange={onChange}
                    customInput={<ManualFeeDateInput />}
                />
            </div>
            {renderManualFeeFieldError(errorMessage)}
        </div>
    );

    const renderManualFeeCompletionDateCell = (
        row: EngagementHistoryRecord
    ): React.JSX.Element =>
        renderManualFeeDatePicker(
            getManualFeeDraft(row).completionDate,
            (value: Date) => updateManualFeeDraft(row, { completionDate: value })
        );

    const formatManualFeeAmountInput = (text: string): string => {
        const parsed = parseCurrencyInput(text);
        return parsed === null ? text : Helpers.GetCurrencyFormat(parsed);
    };

    const renderManualFeeAmountInput = (
        row: EngagementHistoryRecord,
        field: ManualFeeAmountField,
        value: string,
        error?: string
    ): React.JSX.Element => (
        <div className="manual-fee-field">
            <Input
                type="text"
                className={
                    error
                        ? 'manual-fee-input manual-fee-input-invalid'
                        : 'manual-fee-input'
                }
                value={value}
                onChange={(e) =>
                    updateManualFeeDraft(row, { [field]: e.target.value })
                }
                onBlur={(e) =>
                    updateManualFeeDraft(row, {
                        [field]: formatManualFeeAmountInput(e.target.value),
                    })
                }
            />
            {renderManualFeeFieldError(error)}
        </div>
    );

    const renderManualFeeAmountDueCell = (
        row: EngagementHistoryRecord
    ): React.JSX.Element => {
        const draft = getManualFeeDraft(row);
        const errors = validateManualFeeDraft(draft);
        return renderManualFeeAmountInput(
            row,
            'amountDue',
            draft.amountDue,
            errors.amountDue
        );
    };

    const renderManualFeeAmountPaidCell = (
        row: EngagementHistoryRecord
    ): React.JSX.Element => {
        const draft = getManualFeeDraft(row);
        const errors = validateManualFeeDraft(draft);
        return renderManualFeeAmountInput(
            row,
            'amountPaid',
            draft.amountPaid,
            errors.amountPaid
        );
    };

    const renderManualFeePaymentDateCell = (
        row: EngagementHistoryRecord
    ): React.JSX.Element => {
        const draft = getManualFeeDraft(row);
        const errors = validateManualFeeDraft(draft);
        return renderManualFeeDatePicker(
            draft.paymentDate,
            (value: Date) => updateManualFeeDraft(row, { paymentDate: value }),
            errors.paymentDate
        );
    };

    const renderManualFeeRefundCell = (
        row: EngagementHistoryRecord
    ): React.JSX.Element => {
        const draft = getManualFeeDraft(row);
        const errors = validateManualFeeDraft(draft);
        return renderManualFeeAmountInput(
            row,
            'refundAmount',
            draft.refundAmount,
            errors.refundAmount
        );
    };

    const renderManualFeeAmountPaidText = (
        amountPaid?: number
    ): React.JSX.Element => (
        <div className="amount-cell">
            {amountPaid != null ? Helpers.GetCurrencyFormat(amountPaid) : ''}
        </div>
    );

    const renderManualFeeRefundText = (
        refundAmount?: number
    ): React.JSX.Element => (
        <div className="amount-cell">
            {refundAmount > 0 ? Helpers.GetCurrencyFormat(refundAmount) : ''}
        </div>
    );

    const getTimelineEventRecordColumns =
        (): ColumnDescription<EngagementHistoryRecord>[] => [
            { dataField: 'eventId', text: '', hidden: true },
            {
                dataField: 'completionDate',
                text: 'Action/Completion Date',
                sort: true,
                formatter: (cellContent: Date, row: EngagementHistoryRecord) =>
                    isManualFeeRow(row)
                        ? renderManualFeeCompletionDateCell(row)
                        : cellContent
                        ? moment(cellContent).format('L')
                        : 'NA',
            },
            {
                dataField: 'eventName',
                text: 'Event',
                sort: true,
                formatter: (
                    cellContent: string,
                    row: EngagementHistoryRecord
                ) => renderEventCell(row, cellContent),
            },
            {
                hidden: props.viewParty !== TimelineParty.VetValue,
                dataField: 'responsibleParty',
                text: 'Responsible Party',
                sort: true,
                formatter: (
                    cellContent: TimelineParty,
                    row: EngagementHistoryRecord
                ) => {
                    if (cellContent === TimelineParty.Seller) {
                        return (
                            TimelineParties.find((f) => f.id === cellContent)
                                ?.name ?? ''
                        );
                    }

                    return (
                        <div
                            className="link-text"
                            onClick={() => {
                                if (row.buyerId) {
                                    openBuyerProfile(row.buyerId);
                                }
                            }}>
                            {row.buyerName}
                        </div>
                    );
                },
            },
            {
                dataField: 'deliverableName',
                text: 'Deliverable',
                sort: true,
                formatter: (
                    cellContent: string,
                    row: EngagementHistoryRecord
                ) => {
                    return renderDeliverableCell(row, cellContent);
                },
            },
            {
                dataField: 'feeDetails.feeName',
                text: 'Fee Type',
                sort: true,
                formatter: (
                    cellContent: string,
                    row: EngagementHistoryRecord
                ) => {
                    return renderFeeCell(row, cellContent);
                },
            },
            {
                dataField: 'feeDetails.amountDue',
                text: 'Amount Due',
                sort: true,
                formatter: (
                    cellContent: number,
                    row: EngagementHistoryRecord
                ) =>
                    isManualFeeRow(row) ? (
                        renderManualFeeAmountDueCell(row)
                    ) : isPaymentRow(row) ? (
                        <div className="amount-cell">
                            {formatFeeAmount(row, cellContent)}
                        </div>
                    ) : (
                        <></>
                    ),
            },
            {
                dataField: 'feeDetails.amountPaid',
                text: 'Amount Paid',
                sort: true,
                formatter: (
                    cellContent: number,
                    row: EngagementHistoryRecord
                ) => {
                    if (isManualFeeRow(row)) {
                        return renderManualFeeAmountPaidCell(row);
                    }

                    if (isManualFeeRecord(row)) {
                        return renderManualFeeAmountPaidText(
                            row.feeDetails?.amountPaid
                        );
                    }

                    const isBrokerOrObserver =
                        props.viewParty === TimelineParty.Broker ||
                        props.viewParty === TimelineParty.Observer;
                    const canOpenReceipt =
                        !props.isReadonly && !isBrokerOrObserver;

                    return isPaymentRow(row) ? (
                        <div
                            className={
                                canOpenReceipt
                                    ? 'link-text amount-cell'
                                    : 'amount-cell'
                            }
                            onClick={() => {
                                if (canOpenReceipt) {
                                    const transactionId =
                                        row.feeDetails?.transactionIds.find(
                                            (f) => f
                                        );
                                    openReceiptPopup(transactionId, row.userId);
                                }
                            }}>
                            {formatFeeAmount(row, cellContent)}
                        </div>
                    ) : (
                        <></>
                    );
                },
            },
            {
                dataField: 'feeDetails.paymentDate',
                text: 'Payment Date',
                sort: true,
                formatter: (cellContent: Date, row: EngagementHistoryRecord) =>
                    isManualFeeRow(row) ? (
                        renderManualFeePaymentDateCell(row)
                    ) : row.feeDetails?.isFeeAvailable && cellContent ? (
                        <div className="amount-cell">
                            {moment(cellContent).format('L')}
                        </div>
                    ) : (
                        <></>
                    ),
            },
            {
                text: 'Refund',
                dataField: 'feeDetails.refundAmount',
                headerFormatter: () => {
                    return (
                        <>
                            {'Refund '}
                            <InfoTooltip
                                idText="engagement-history-refunds-header-tooltip"
                                text="Refunds are provided in the form of Store Credits, deposited directly into your wallet"></InfoTooltip>
                        </>
                    );
                },
                sort: true,
                formatter: (
                    cellContent: number,
                    row: EngagementHistoryRecord
                ) => {
                    if (isManualFeeRow(row)) {
                        return renderManualFeeRefundCell(row);
                    }

                    if (isManualFeeRecord(row)) {
                        return renderManualFeeRefundText(row.feeDetails?.refundAmount);
                    }

                    const isVisible =
                        row.feeDetails?.feeDetailsType ===
                            EngagementHistoryRecordFeeDetailTypes.RefundInfo ||
                        row.feeDetails?.feeDetailsType ===
                            EngagementHistoryRecordFeeDetailTypes.Summary;

                    const isBrokerOrObserverRefunds =
                        props.viewParty === TimelineParty.Broker ||
                        props.viewParty === TimelineParty.Observer;

                    return isVisible ? (
                        <div
                            className="link-text amount-cell"
                            onClick={() => {
                                if (!isBrokerOrObserverRefunds) {
                                    const transactionId =
                                        row.feeDetails?.transactionIds.find(
                                            (f) => f
                                        );
                                    const payeeId = row.userId;
                                    openReceiptPopup(transactionId, payeeId);
                                }
                            }}>
                            {row.feeDetails?.isFeeAvailable &&
                            cellContent != null
                                ? Helpers.GetCurrencyFormat(cellContent)
                                : ''}
                        </div>
                    ) : (
                        <></>
                    );
                },
            },
            {
                hidden: props.viewParty === TimelineParty.Buyer,
                dataField: 'feeDetails.creditableAmount',
                text: 'Creditable Amount',
                headerFormatter: () => {
                    return (
                        <>
                            {'Creditable Amount'}
                            <div>
                                {`Total Credits: ${Helpers.GetCurrencyFormat(
                                    totalCreditableAmount
                                )}`}
                            </div>
                        </>
                    );
                },
                sort: true,
                formatter: (
                    cellContent: number,
                    row: EngagementHistoryRecord
                ) => {
                    return isPaymentRow(row) ? (
                        <div className="amount-cell">
                            {row.feeDetails?.isFeeAvailable &&
                            cellContent != null &&
                            cellContent > 0
                                ? Helpers.GetCurrencyFormat(cellContent)
                                : ''}
                        </div>
                    ) : (
                        <></>
                    );
                },
            },
            {
                dataField: ActionColumnField,
                text: 'Action',
                hidden: props.viewParty === TimelineParty.Buyer,
                sort: false,
                formatter: (_cellContent: any, row: EngagementHistoryRecord) => {
                    const showInvoiceDownload =
                        isPostedManualFeeInvoiceRow(row);

                    if (isManualFeeRow(row)) {
                        return (
                            <div className="manual-fee-action-cell">
                                <Button
                                    color="primary"
                                    size="sm"
                                    disabled={
                                        hasManualFeeErrors(row) ||
                                        isManualFeeSaving(row)
                                    }
                                    onClick={() => handleManualFeeUpdate(row)}>
                                    {row.manualFeeState ===
                                    ManualFeeState.Placeholder
                                        ? 'Post'
                                        : 'Update'}
                                </Button>
                                {showInvoiceDownload &&
                                    renderManualFeeInvoiceViewButton(row)}
                            </div>
                        );
                    }

                    return showInvoiceDownload ? (
                        <div className="manual-fee-action-cell">
                            {renderManualFeeInvoiceViewButton(row)}
                        </div>
                    ) : (
                        <></>
                    );
                },
            },
        ];

    const handleTableChange = (_type: any, newState: TableSortingState) => {
        const newSortingOption: SortingOption = {
            dataField: newState.sortField,
            order: newState.sortOrder as SortOrder,
        };
        setSortingOption(newSortingOption);
        updateTableData(newState.data, newSortingOption);
    };

    const updateTableData = (
        data: EngagementHistoryRecord[],
        sortingOption: SortingOption
    ) => {
        const countZeroAsEmpty = sortingOption.dataField.includes('feeDetails');
        const sortedHistory = sortingFunction(
            data,
            sortingOption,
            true,
            countZeroAsEmpty
        );
        setTimelineEventsList(sortedHistory);
    };

    const renderCellContent = (
        row: EngagementHistoryRecord,
        index: number,
        columnDefinition: ColumnDescription<EngagementHistoryRecord>
    ) => {
        const cellContent =
            getNestedValue(row, columnDefinition.dataField) ?? '';

        if (columnDefinition.formatter) {
            return (
                <td>{columnDefinition.formatter(cellContent, row, index)}</td>
            );
        }

        return <td>{cellContent.toString()}</td>;
    };

    const getTimelineEventsTableBody = () => {
        return (
            <tbody>
                {timelineEventsList.map(
                    (record: EngagementHistoryRecord, index) => {
                        return (
                            <tr
                                key={
                                    isManualFeeRow(record)
                                        ? getManualFeeRowKey(record)
                                        : index
                                }>
                                {getTimelineEventRecordColumns()
                                    .filter((f) => !f.hidden)
                                    .map(
                                        (
                                            columnDefinition: ColumnDescription<EngagementHistoryRecord>
                                        ) =>
                                            renderCellContent(
                                                record,
                                                index,
                                                columnDefinition
                                            )
                                    )}
                            </tr>
                        );
                    }
                )}
            </tbody>
        );
    };

    const renderFilterBar = () => {
        const deliverableOptions = Array.from(
            new Set(
                (engagementHistoy?.timelineEvents ?? [])
                    .map((x: EngagementHistoryRecord) => x.deliverableName)
                    .filter((x) => x)
            )
        ) as string[];

        return (
            <div className="filters-bar">
                <FormGroup className="filter-scroller">
                    <div
                        onClick={() => {
                            execStartRef.current?.setOpen(true);
                        }}
                        className="filter-item">
                        <label>Action/Completion Date Start</label>
                        <div className="d-flex small-border">
                            <InputGroupAddon
                                addonType="prepend"
                                onClick={() => {
                                    execStartRef.current?.setOpen(true);
                                }}>
                                <div className="input-group-text">
                                    <FontAwesomeIcon icon={faCalendarAlt} />
                                </div>
                            </InputGroupAddon>
                            <DatePicker
                                ref={execStartRef}
                                selected={execStartDate}
                                name="execStartDate"
                                dateFormat="MM/d/yyyy"
                                onChange={(v: Date) => handleExecStartChange(v)}
                                isClearable
                            />
                        </div>
                    </div>

                    <div className="dash-separator">{'-'}</div>

                    <div
                        onClick={() => {
                            execEndRef.current?.setOpen(true);
                        }}
                        className="filter-item">
                        <label>Action/Completion Date End</label>
                        <div className="d-flex small-border">
                            <InputGroupAddon
                                addonType="prepend"
                                onClick={() => {
                                    execEndRef.current?.setOpen(true);
                                }}>
                                <div className="input-group-text">
                                    <FontAwesomeIcon icon={faCalendarAlt} />
                                </div>
                            </InputGroupAddon>
                            <DatePicker
                                ref={execEndRef}
                                selected={execEndDate}
                                name="execEndDate"
                                dateFormat="MM/d/yyyy"
                                onChange={(v: Date) => handleExecEndChange(v)}
                                isClearable
                            />
                        </div>
                    </div>

                    <div className="line-separator"></div>

                    <div className="filter-item member-input">
                        <label>Event</label>
                        <Input
                            value={eventFilter}
                            type="text"
                            onChange={(e) =>
                                handleEventFilterChange(e.target.value)
                            }
                        />
                    </div>

                    {props.viewParty === TimelineParty.VetValue && (
                        <>
                            <div className="line-separator"></div>
                            <div className="filter-item">
                                <label>Responsible Party</label>
                                <div className="customerStyle">
                                    <select
                                        className="selectCustomer"
                                        value={partyFilter}
                                        onChange={(e) =>
                                            handlePartyFilterChange(
                                                e.target.value
                                            )
                                        }>
                                        <option value="">All</option>
                                        {TimelineParties.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="line-separator"></div>

                    <div className="filter-item">
                        <label>Deliverable</label>
                        <div className="customerStyle">
                            <select
                                className="selectCustomer"
                                value={deliverableFilter}
                                onChange={(e) =>
                                    handleDeliverableFilterChange(
                                        e.target.value
                                    )
                                }>
                                <option value="">All</option>
                                {deliverableOptions.map((d) => (
                                    <option key={d} value={d}>
                                        {d}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </FormGroup>
            </div>
        );
    };

    const applyFilters = () => {
        const source = engagementHistoy?.timelineEvents ?? [];
        const filtered = source.filter((r: EngagementHistoryRecord) => {
            const ed = r.completionDate ? new Date(r.completionDate) : null;

            if (execStartDate && (!ed || ed < new Date(execStartDate)))
                return false;
            if (execEndDate) {
                const e = new Date(execEndDate);
                e.setHours(23);
                e.setMinutes(59);
                e.setSeconds(59);
                e.setMilliseconds(999);
                if (!ed || ed > e) return false;
            }

            if (
                eventFilter &&
                !(r.eventName ?? '')
                    .toLowerCase()
                    .includes(eventFilter.toLowerCase())
            )
                return false;

            if (partyFilter !== '' && r.responsibleParty !== partyFilter)
                return false;

            if (
                deliverableFilter &&
                (r.deliverableName ?? '') !== deliverableFilter
            )
                return false;

            return true;
        });

        updateTableData(filtered, sortingOption);
    };

    useEffect(() => {
        applyFilters();
    }, [
        execStartDate,
        execEndDate,
        eventFilter,
        partyFilter,
        deliverableFilter,
        engagementHistoy,
        sortingOption,
    ]);

    const handleExecStartChange = (v: Date | null) => {
        if (v && execEndDate) {
            const a = new Date(v.getFullYear(), v.getMonth(), v.getDate());
            const b = new Date(
                execEndDate.getFullYear(),
                execEndDate.getMonth(),
                execEndDate.getDate()
            );
            if (a > b) {
                const newEnd = new Date(v);
                newEnd.setHours(23);
                newEnd.setMinutes(59);
                setExecStartDate(execEndDate);
                setExecEndDate(newEnd);
                return;
            }
        }
        setExecStartDate(v);
    };

    const handleExecEndChange = (v: Date | null) => {
        let end = v ? new Date(v) : null;
        if (end) {
            end.setHours(23);
            end.setMinutes(59);
        }
        if (end && execStartDate) {
            const a = new Date(
                execStartDate.getFullYear(),
                execStartDate.getMonth(),
                execStartDate.getDate()
            );
            const b = new Date(
                end.getFullYear(),
                end.getMonth(),
                end.getDate()
            );
            if (a > b) {
                const newEnd = execStartDate ? new Date(execStartDate) : null;
                if (newEnd) {
                    newEnd.setHours(23);
                    newEnd.setMinutes(59);
                }
                setExecStartDate(end);
                setExecEndDate(newEnd);
                return;
            }
        }
        setExecEndDate(end);
    };

    const handleEventFilterChange = (v: string) => {
        setEventFilter(v);
    };

    const handlePartyFilterChange = (v: string) => {
        setPartyFilter(v === '' ? '' : Number(v));
    };

    const handleDeliverableFilterChange = (v: string) => {
        setDeliverableFilter(v);
    };

    return (
        <div className="engagement-history-table">
            <GetReceiptByDateRange
                isAdmin={props.viewParty === TimelineParty.VetValue}
                userIds={getEngagementUserIds()}
                engagementId={props.engagementId}
            />
            {renderFilterBar()}

            <div className="table-container">
                <table className="table">
                    <thead>
                        <tr>
                            {getTimelineEventRecordColumns()
                                .filter((f) => !f.hidden)
                                .map((columnDefinition: ColumnDescription) => {
                                    const columnSortOrder =
                                        sortingOption.dataField ===
                                        columnDefinition.dataField
                                            ? sortingOption.order
                                            : undefined;
                                    return (
                                        <th
                                            className={
                                                columnDefinition.sort
                                                    ? 'sortable'
                                                    : ''
                                            }
                                            onClick={() => {
                                                if (!columnDefinition.sort) {
                                                    return;
                                                }
                                                const tableChangeState: TableSortingState =
                                                    {
                                                        data: timelineEventsList,
                                                        sortField:
                                                            columnDefinition.dataField,
                                                        sortOrder:
                                                            columnSortOrder ===
                                                            'asc'
                                                                ? 'desc'
                                                                : 'asc',
                                                    };
                                                handleTableChange(
                                                    'sort',
                                                    tableChangeState
                                                );
                                            }}>
                                            {columnDefinition.headerFormatter
                                                ? columnDefinition.headerFormatter(
                                                      columnDefinition,
                                                      null,
                                                      null
                                                  )
                                                : `${columnDefinition.text}`}
                                            {columnDefinition.sort && (
                                                <span>
                                                    <SortingSymbol
                                                        order={columnSortOrder}
                                                    />
                                                </span>
                                            )}
                                        </th>
                                    );
                                })}
                        </tr>
                    </thead>
                    {getTimelineEventsTableBody()}
                </table>
            </div>

            {isReceiptVisible && (
                <ReceiptPopup
                    canNavigateToTransaction={props.canNavigateToTransaction}
                    isVisible={isReceiptVisible}
                    onClose={() => {
                        setIsReceiptVisible(false);
                    }}
                    mode={ReceiptViewMode.TransactionView}
                    transactionId={selectedTransactionId}
                    userId={selectedInvoicePayee}
                    isThirdPartyView={
                        props.viewParty === TimelineParty.VetValue ||
                        props.viewParty === TimelineParty.Broker ||
                        props.viewParty === TimelineParty.Observer
                    }
                />
            )}

            {isBuyerProfilePopupVisible ? (
                <BuyerProfilePreviewPopup
                    buyerId={selectedBuyerId}
                    close={closeBuyerProfile}
                />
            ) : (
                <></>
            )}

            {loiPreviewFormId && (
                <Modal
                    isOpen={!!loiPreviewFormId}
                    toggle={() => setLoiPreviewFormId(null)}
                    size="xl"
                    className="loi-preview-modal">
                    <ModalHeader toggle={() => setLoiPreviewFormId(null)}>
                        LOI Form Preview
                    </ModalHeader>
                    <ModalBody>
                        <LoiSubmissionPackage
                            loiFormId={loiPreviewFormId}
                            isReadonly={true}
                            canSubmit={false}
                            engagementId={props.engagementId}
                        />
                    </ModalBody>
                </Modal>
            )}

            <Rodal
                visible={!!manualFeeConfirmRow}
                onClose={closeManualFeeConfirm}
                animation="slideUp"
                className="manual-fee-confirm-popup"
                showMask={true}
                width={ManualFeeConfirmPopupWidth}
                showCloseButton={true}>
                <ModalHeader>
                    {isManualFeePlaceholder(manualFeeConfirmRow)
                        ? 'Post Monthly Engagement Fee'
                        : 'Update Monthly Engagement Fee'}
                </ModalHeader>
                <ModalBody>
                    <div className="main-text">
                        {isAdvisorCompanyMissing
                            ? ManualFeeMissingAdvisorCompanyText
                            : isManualFeePlaceholder(manualFeeConfirmRow)
                              ? 'The seller will see this fee in their Engagement History.'
                              : 'The posted values will be overwritten.'}
                    </div>
                    <div className="manual-fee-confirm-buttons">
                        <button
                            className="btn btn-outline-secondary"
                            onClick={closeManualFeeConfirm}>
                            Cancel
                        </button>
                        <ButtonLoader
                            className="btn btn-primary"
                            buttonText={
                                isManualFeePlaceholder(manualFeeConfirmRow)
                                    ? 'Post Fee'
                                    : 'Update Fee'
                            }
                            loaderButtonText=""
                            disabled={
                                isAdvisorCompanyMissing ||
                                (!!manualFeeConfirmRow &&
                                    isManualFeeSaving(manualFeeConfirmRow))
                            }
                            isLoading={
                                !!manualFeeConfirmRow &&
                                isManualFeeSaving(manualFeeConfirmRow)
                            }
                            onClick={confirmManualFeeSave}
                        />
                    </div>
                </ModalBody>
            </Rodal>

            <ManualFeeInvoicePopup
                isVisible={!!manualFeeInvoiceRow}
                onClose={closeManualFeeInvoice}
                engagementId={props.engagementId}
                historyRecordId={manualFeeInvoiceRow?.historyRecordId}
            />
        </div>
    );
};

export default EngagementHistoryTable;
